const markerGap = 10;
const collisionGap = 6;
const supportedSides = ['right', 'left', 'bottom', 'top'];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getBounds = (rect) => ({
  left: rect.left,
  top: rect.top,
  right: rect.left + rect.width,
  bottom: rect.top + rect.height,
});

const getOverflow = (rect, bounds) => {
  const rectBounds = getBounds(rect);
  const targetBounds = getBounds(bounds);

  return Math.max(targetBounds.left - rectBounds.left, 0)
    + Math.max(rectBounds.right - targetBounds.right, 0)
    + Math.max(targetBounds.top - rectBounds.top, 0)
    + Math.max(rectBounds.bottom - targetBounds.bottom, 0);
};

const getSideOrder = (preferredSide) => {
  const side = supportedSides.includes(preferredSide) ? preferredSide : 'right';
  const opposite = {
    right: 'left',
    left: 'right',
    bottom: 'top',
    top: 'bottom',
  }[side];
  const perpendicular = ['right', 'left'].includes(side)
    ? ['bottom', 'top']
    : ['right', 'left'];

  return [side, opposite, ...perpendicular];
};

const getCrossAxisStart = (center, markerSize, labelSize, align) => {
  if (align === 'start') {
    return center - (markerSize / 2);
  }

  if (align === 'end') {
    return center + (markerSize / 2) - labelSize;
  }

  return center - (labelSize / 2);
};

const createCandidate = ({ pin, label, side, align, offsetX, offsetY }) => {
  const markerRadius = pin.size / 2;
  let left = getCrossAxisStart(pin.x, pin.size, label.width, align);
  let top = getCrossAxisStart(pin.y, pin.size, label.height, align);

  if (side === 'right') {
    left = pin.x + markerRadius + markerGap;
  }

  if (side === 'left') {
    left = pin.x - markerRadius - markerGap - label.width;
  }

  if (side === 'bottom') {
    top = pin.y + markerRadius + markerGap;
  }

  if (side === 'top') {
    top = pin.y - markerRadius - markerGap - label.height;
  }

  return {
    side,
    left: left + offsetX,
    top: top + offsetY,
    width: label.width,
    height: label.height,
  };
};

export const rectsOverlap = (first, second, gap = collisionGap) => {
  const firstBounds = getBounds(first);
  const secondBounds = getBounds(second);

  return firstBounds.left < secondBounds.right + gap
    && firstBounds.right + gap > secondBounds.left
    && firstBounds.top < secondBounds.bottom + gap
    && firstBounds.bottom + gap > secondBounds.top;
};

export const getMapMobileFocus = ({ labels, markers = [], viewportWidth, stageWidth, inset = 10 }) => {
  const visibleElements = [...labels, ...markers].filter((element) => (
    Number.isFinite(element.left) && Number.isFinite(element.width)
  ));

  if (visibleElements.length === 0) {
    return {
      left: 0,
      right: 0,
      width: 0,
      scrollLeft: 0,
    };
  }

  const left = Math.min(...visibleElements.map((element) => element.left));
  const right = Math.max(...visibleElements.map((element) => element.left + element.width));
  const maxScroll = Math.max(stageWidth - viewportWidth, 0);

  return {
    left,
    right,
    width: right - left,
    scrollLeft: clamp(left - inset, 0, maxScroll),
  };
};

export const chooseLabelPlacement = ({
  pin,
  label,
  stage,
  viewport = stage,
  preferred = {},
  occupied = [],
}) => {
  const sideOrder = getSideOrder(preferred.side);
  const align = ['start', 'center', 'end'].includes(preferred.align) ? preferred.align : 'center';
  const offsetX = Number.isFinite(Number(preferred.offsetX)) ? Number(preferred.offsetX) : 0;
  const offsetY = Number.isFinite(Number(preferred.offsetY)) ? Number(preferred.offsetY) : 0;
  const pinIsVisible = pin.x >= viewport.left
    && pin.x <= viewport.left + viewport.width
    && pin.y >= viewport.top
    && pin.y <= viewport.top + viewport.height;
  const candidates = sideOrder.map((side, index) => {
    const isPreferred = index === 0;
    const candidate = createCandidate({
      pin,
      label,
      side,
      align: isPreferred ? align : 'center',
      offsetX: isPreferred ? offsetX : 0,
      offsetY: isPreferred ? offsetY : 0,
    });
    const stageOverflow = getOverflow(candidate, stage);
    const viewportOverflow = pinIsVisible ? getOverflow(candidate, viewport) : 0;
    const collisions = occupied.filter((rect) => rectsOverlap(candidate, rect)).length;

    return {
      ...candidate,
      score: (stageOverflow * 1000000000)
        + (collisions * 10000000)
        + (viewportOverflow * 10000)
        + index,
    };
  });
  const best = candidates.reduce((current, candidate) => (
    candidate.score < current.score ? candidate : current
  ));
  const maximumLeft = stage.left + Math.max(stage.width - label.width, 0);
  const maximumTop = stage.top + Math.max(stage.height - label.height, 0);

  return {
    side: best.side,
    left: Math.min(Math.max(best.left, stage.left), maximumLeft),
    top: Math.min(Math.max(best.top, stage.top), maximumTop),
    width: best.width,
    height: best.height,
  };
};
